import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import AppLayout from "@/components/AppLayout";
import { EmailMask } from "@/components/EmailMask";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { GraduationCap, Plus, Trash2, Loader2, Mail } from "lucide-react";
import { userApi, User as UserType, CreateUserData } from "@/lib/api/admin";
import { getErrorMessage } from "@/lib/api/client";
import { useToast } from "@/components/ui/use-toast";

export default function AdminStudentManagementPage() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [searchTerm, setSearchTerm] = useState("");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [formData, setFormData] = useState<CreateUserData>({
    email: "", password: "", first_name: "", last_name: "", role: "student",
  });

  const { data: students, isLoading } = useQuery({ queryKey: ["students"], queryFn: () => userApi.list("student") });

  const createMutation = useMutation({
    mutationFn: userApi.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["students"] });
      queryClient.invalidateQueries({ queryKey: ["adminCounts"] });
      toast({ title: "Success", description: "Student created successfully" });
      handleCloseDialog();
    },
    onError: (error) => toast({ variant: "destructive", title: "Error", description: getErrorMessage(error) }),
  });

  const deleteMutation = useMutation({
    mutationFn: userApi.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["students"] });
      queryClient.invalidateQueries({ queryKey: ["adminCounts"] });
      toast({ title: "Success", description: "Student deleted successfully" });
    },
    onError: (error) => toast({ variant: "destructive", title: "Error", description: getErrorMessage(error) }),
  });

  const filteredStudents = (students || []).filter(
    (student) =>
      (student.first_name ?? "").toLowerCase().includes(searchTerm.toLowerCase()) ||
      (student.last_name ?? "").toLowerCase().includes(searchTerm.toLowerCase()) ||
      (student.email ?? "").toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleOpenDialog = () => {
    setFormData({ email: "", password: "", first_name: "", last_name: "", role: "student" });
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setFormData({ email: "", password: "", first_name: "", last_name: "", role: "student" });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    createMutation.mutate(formData);
  };

  const handleDelete = (id: string) => {
    if (!confirm("Are you sure you want to delete this student?")) return;
    deleteMutation.mutate(id);
  };

  return (
    <AppLayout role="admin">
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-foreground">Student Management</h1>
            <p className="text-muted-foreground">View and manage all students across schools</p>
          </div>
          <Button onClick={handleOpenDialog}><Plus className="w-4 h-4 mr-2" />Add Student</Button>
        </div>

        <div className="flex gap-4">
          <div className="flex-1"><Input placeholder="Search students..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="max-w-sm" /></div>
        </div>

        <Card>
          <CardHeader><CardTitle>Students</CardTitle><CardDescription>All registered students across all schools</CardDescription></CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="flex justify-center py-8"><Loader2 className="w-8 h-8 animate-spin text-muted-foreground" /></div>
            ) : filteredStudents.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground"><GraduationCap className="w-12 h-12 mx-auto mb-4 opacity-50" /><p>No students found</p></div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow><TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead></TableRow>
                </TableHeader>
                <TableBody>
                  {filteredStudents.map((student) => (
                    <TableRow key={student.id}>
                      <TableCell className="font-medium">{student.first_name || ""} {student.last_name || ""}</TableCell>
                      <TableCell><EmailMask email={student.email || ""} /></TableCell>
                      <TableCell><span className={`inline-flex items-center px-2 py-1 rounded-full text-xs ${student.is_active ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}`}>{student.is_active ? "Active" : "Inactive"}</span></TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button variant="ghost" size="icon" onClick={() => handleDelete(student.id)} disabled={deleteMutation.isPending}>
                            {deleteMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4 text-destructive" />}
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogContent>
            <DialogHeader><DialogTitle>Add Student</DialogTitle><DialogDescription>Create a new student account</DialogDescription></DialogHeader>
            <form onSubmit={handleSubmit}>
              <div className="grid gap-4 py-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="grid gap-2"><Label htmlFor="first_name">First Name</Label><Input id="first_name" value={formData.first_name} onChange={(e) => setFormData({ ...formData, first_name: e.target.value })} required /></div>
                  <div className="grid gap-2"><Label htmlFor="last_name">Last Name</Label><Input id="last_name" value={formData.last_name} onChange={(e) => setFormData({ ...formData, last_name: e.target.value })} required /></div>
                </div>
                <div className="grid gap-2"><Label htmlFor="email">Email</Label><Input id="email" type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} required /></div>
                <div className="grid gap-2"><Label htmlFor="password">Password</Label><Input id="password" type="password" value={formData.password} onChange={(e) => setFormData({ ...formData, password: e.target.value })} required minLength={12} placeholder="Minimum 12 characters" /></div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={handleCloseDialog} disabled={createMutation.isPending}>Cancel</Button>
                <Button type="submit" disabled={createMutation.isPending}>{createMutation.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Create</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </AppLayout>
  );
}
